/**
 * OAuth Service for WayFong Authentication
 *
 * Handles communication with the WayFong Auth service for domain validation.
 * This is Layer 1 of authentication: validating the user is from an authorized
 * domain (wayfong.com / wallyco.net).
 *
 * Token model:
 *  - access_token: 15-min lifetime, kept in memory by OAuthProvider (NOT in
 *    any browser storage — minimizes XSS blast radius).
 *  - refresh_token: 7-day lifetime, set as an HttpOnly cookie by the wfauth
 *    server (Domain=.wallyco.net). JS cannot read it; the browser attaches
 *    it automatically on `credentials: 'include'` calls to auth.wallyco.net.
 *  - user info (email/name): stored in localStorage as cached display
 *    metadata only; never used as a credential.
 *
 * Refresh-on-load: on every page load, the client calls /auth/token/refresh
 * with credentials. The cookie buys us a fresh access_token without an OAuth
 * round-trip. Only a definitive 401 (cookie expired or absent) sends the
 * user through Google again.
 */

import {
  OAUTH_ENDPOINTS,
  OAUTH_STORAGE_KEYS,
  OAUTH_CALLBACK_PATH,
  WAYFONG_AUTH_URL,
} from '../config/oauth';

// User info returned from OAuth validation (email/name/picture).
// Cached in localStorage strictly for display while the access token lives in
// memory; never sent as proof of identity.
export interface OAuthUser {
  email: string;
  name?: string;
  picture?: string;
}

// Response from token validation endpoint.
// `transient: true` means validation could not be completed (network/5xx) and
// the token may still be valid — callers MUST NOT delete the token in that case.
interface TokenValidationResponse {
  valid: boolean;
  user?: OAuthUser;
  error?: string;
  transient?: boolean;
  // WCO-2914: whether the user holds HTE access (`hte:<env>:*:access`). Undefined when
  // the backend gate is dormant (no wf-auth secret configured) → treat as allowed.
  hasHteAccess?: boolean;
}

// WCO-2914: the current wf-auth access token, published for the API client to attach as
// `x-wayfong-token` so the api-gateway can re-check HTE access server-side. Kept in a module
// variable (in memory), NOT localStorage — preserves the "OAuth token never persisted" posture.
let apiWfAuthToken: string | null = null;
export function setApiWfAuthToken(token: string | null): void {
  apiWfAuthToken = token;
}
export function getApiWfAuthToken(): string | null {
  return apiWfAuthToken;
}

// WCO-2914: the gateway re-checks HTE access on EVERY /api route, so every same-origin /api request
// needs the wf-auth token — not only the two axios instances that attach it themselves. Returns the
// header for such a URL, and nothing for any other origin (e.g. a local print service), so the token
// never leaves this site.
export function wfAuthHeaderFor(url: string): Record<string, string> {
  if (!apiWfAuthToken) return {};
  let target: URL;
  try {
    target = new URL(url, window.location.href);
  } catch {
    return {};
  }
  if (target.origin !== window.location.origin || !/^\/api(\/|$)/i.test(target.pathname)) return {};
  return { 'x-wayfong-token': `Bearer ${apiWfAuthToken}` };
}

/**
 * Validate an OAuth token with the WayFong Auth service.
 * Returns `{ valid: false, transient: true }` for network/5xx errors so callers
 * can keep the token across temporary outages instead of forcing re-auth.
 */
export async function validateToken(token: string): Promise<TokenValidationResponse> {
  try {
    const response = await fetch(OAUTH_ENDPOINTS.TOKEN_VALIDATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        return { valid: false, error: 'Token expired or invalid' };
      }
      return {
        valid: false,
        error: `Validation failed: ${response.status}`,
        transient: true,
      };
    }

    const data = await response.json();
    return {
      valid: true,
      user: data.user || data,
      hasHteAccess: data.hasHteAccess,
    };
  } catch (error) {
    console.error('OAuth token validation error:', error);
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Validation failed',
      transient: true,
    };
  }
}

// Result shape for refreshAccessToken — same discriminator as validateToken so
// the caller can treat 5xx/network errors as "try again later" while 401 means
// "cookie's gone, send the user back through OAuth."
export interface RefreshResult {
  ok: boolean;
  accessToken?: string;
  expiresIn?: number;
  error?: string;
  transient?: boolean;
}

/**
 * Silently exchange the HttpOnly refresh cookie for a new access_token.
 * The browser attaches the cookie automatically; this call carries no body
 * and no Authorization header. CORS requires the TnB origin to be in the
 * wfauth `CORS_ORIGINS` allowlist (Doppler `wf-goauth`).
 *
 * - 200 → `{ ok: true, accessToken, expiresIn }`
 * - 401 → `{ ok: false }` (definitive: cookie expired or missing — re-OAuth needed)
 * - 5xx / network → `{ ok: false, transient: true }` (caller should retry, not log out)
 */
export async function refreshAccessToken(): Promise<RefreshResult> {
  try {
    const response = await fetch(`${WAYFONG_AUTH_URL}/auth/token/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        return { ok: false, error: 'Refresh token expired or missing' };
      }
      return {
        ok: false,
        error: `Refresh failed: ${response.status}`,
        transient: true,
      };
    }

    const data: { access_token: string; token_type?: string; expires_in?: number } =
      await response.json();
    return {
      ok: true,
      accessToken: data.access_token,
      expiresIn: typeof data.expires_in === 'number' ? data.expires_in : undefined,
    };
  } catch (error) {
    console.error('OAuth token refresh error:', error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Refresh failed',
      transient: true,
    };
  }
}

/**
 * Initiate OAuth flow by redirecting to WayFong Auth.
 * Uses redirect_uri set to window.location.origin + callback path so the
 * auth server knows where to return the authorization code.
 * @param returnUrl - URL to return to after OAuth (defaults to current location)
 */
export function initiateOAuth(returnUrl?: string): void {
  const redirectUrl = returnUrl || window.location.pathname + window.location.search;
  localStorage.setItem(OAUTH_STORAGE_KEYS.REDIRECT_URL, redirectUrl);

  const callbackUrl = `${window.location.origin}${OAUTH_CALLBACK_PATH}`;
  const authUrl = `${OAUTH_ENDPOINTS.GOOGLE_AUTH}?redirect_uri=${encodeURIComponent(callbackUrl)}`;
  window.location.href = authUrl;
}

/**
 * Extract authorization code from URL query params after OAuth callback.
 * The code is passed as: ?code=xxx
 */
export function extractCodeFromQuery(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}

// Returned by exchangeCodeForToken. expiresIn is the value WayFong/Google
// reported via `expires_in` (seconds); may be missing if upstream omits it.
export interface ExchangedToken {
  accessToken: string;
  expiresIn?: number;
}

/**
 * Exchange an authorization code for an access token via the WayFong Auth service.
 * Sends credentials (cookies) so the wfauth server can set the HttpOnly
 * refresh_token cookie. Returns the access token plus the upstream-reported
 * lifetime so callers can schedule a proactive refresh before it expires.
 */
export async function exchangeCodeForToken(code: string): Promise<ExchangedToken | null> {
  try {
    const response = await fetch(`${WAYFONG_AUTH_URL}/auth/token/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      console.error('Token exchange failed:', response.status);
      return null;
    }

    const data: { access_token: string; token_type: string; expires_in?: number } =
      await response.json();
    return {
      accessToken: data.access_token,
      expiresIn: typeof data.expires_in === 'number' ? data.expires_in : undefined,
    };
  } catch (error) {
    console.error('Token exchange error:', error);
    return null;
  }
}

/**
 * Get cached OAuth user info (display metadata only — not a credential).
 */
export function getStoredUser(): OAuthUser | null {
  const userJson = localStorage.getItem(OAUTH_STORAGE_KEYS.USER_INFO);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

/**
 * Cache OAuth user info for display.
 */
export function storeUser(user: OAuthUser): void {
  localStorage.setItem(OAUTH_STORAGE_KEYS.USER_INFO, JSON.stringify(user));
}

/**
 * Clear all client-side OAuth state.
 * The access token lives in memory inside OAuthProvider — only that component
 * can drop it. This helper clears the cached user and defensively removes
 * stale sessionStorage keys left over from prior versions of the client.
 */
export function clearToken(): void {
  localStorage.removeItem(OAUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(OAUTH_STORAGE_KEYS.USER_INFO);
  // Migration: older builds persisted the token in sessionStorage. Sweep those
  // keys so they cannot be picked up by anything else.
  sessionStorage.removeItem('wayfongOAuthAccessToken');
  sessionStorage.removeItem('wayfongOAuthAccessTokenExpiresAt');
}

/**
 * Logout from OAuth service.
 * Server-side: invalidates the access token via the existing proxy, and (with
 * credentials) hits the WayFong logout so the refresh cookie is cleared too.
 * Client-side: callers (OAuthProvider.logout) drop the in-memory token + run
 * clearToken() to wipe the cached user.
 */
export async function logout(token?: string | null): Promise<void> {
  // Always invalidate the refresh cookie at the source — direct browser call
  // so the Set-Cookie clearing reply lands in the right cookie jar.
  try {
    await fetch(`${WAYFONG_AUTH_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.warn('OAuth refresh-cookie logout failed:', error);
  }

  // Also invalidate the access token via our proxy if we still have one
  // in memory — short-circuits any other session reusing it.
  if (token) {
    try {
      await fetch(OAUTH_ENDPOINTS.LOGOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.warn('OAuth access-token logout failed:', error);
    }
  }
}

/**
 * Get the intended redirect URL after OAuth completes
 */
export function getRedirectUrl(): string {
  const url = localStorage.getItem(OAUTH_STORAGE_KEYS.REDIRECT_URL);
  localStorage.removeItem(OAUTH_STORAGE_KEYS.REDIRECT_URL);
  return url || '/';
}
